import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { ArrowRight, Building2, Check, Phone, Plus, RotateCw, User } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AmbulanceIcon from '@/assets/smartcity/ambulance-icon.svg';
import Gmaps from '@/assets/smartcity/gmps-icon.svg';
import IncomingIcon from '@/assets/smartcity/incoming-icon.svg';
import RegisterIcon from '@/assets/smartcity/register-icon.svg';
import AudioPlayerCard from '@/domains/traffic-monitoring/components/AudioPlayerCard/AudioPlayerCard';
import BackLink from '@/domains/traffic-monitoring/components/BackLink/BackLink';
import BubbleEvent from '@/domains/traffic-monitoring/components/BubbleEvent/BubbleEvent';
import ButtonWhatsapp from '@/domains/traffic-monitoring/components/ButtonWhatsapp/ButtonWhatsapp';
import CardMediaFile from '@/domains/traffic-monitoring/components/CardMediaFIle/CardMediaFIle';
import MediaViewerModal, {
  type ViewableMedia,
} from '@/domains/traffic-monitoring/components/MediaViewerModal/MediaViewerModal';
import StatusFilter, {
  type FilterItem,
} from '@/domains/traffic-monitoring/components/StatusFilter/StatusFilter';
import TimelineVertical from '@/domains/traffic-monitoring/components/TimelineVertical/TimelineVertical';
import {
  useAmbulanceReportMedia,
  useReportDetails,
  useReportOfficers,
} from '@/domains/traffic-monitoring/hooks/useReportDetails';
import type {
  ReportDetailsHeaderData,
  ReportPatientData,
} from '@/domains/traffic-monitoring/types/report-details.types';
import Header from '@/shared/components/layout/Header/Header';
import Button from '@/shared/components/ui/Button';
import CodeSearchSelect from '@/shared/components/ui/CodeSearchSelect/CodeSearchSelect';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { formatFullDateTimeDDMMYYYY, parseDDMMYYYYToISO } from '@/shared/utils/date';
import { zoomSafeDatepickerPosition } from '@/shared/utils/datepickerPosition';
import { toWhatsAppNumber } from '@/shared/utils/phone';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';

// media_files has no field distinguishing an incoming-call recording from a
// voice note (both are MEDIA_TP_REC) - see useReportDetails.ts. Every
// recording is surfaced under "Voice Notes"; this section has no data source.
const incomingList: { id: number; title: string; audioUrl: string }[] = [];

// cm_code groups behind this page's dropdowns. City/regency has no constant of
// its own: its upper code *is* the selected province's code ('33' -> '3315'),
// so it is only knowable at runtime.
const EXTENSION_UPPER_CODE = 'EXT_ID_TP';
const SERVICE_CATEGORY_UPPER_CODE = 'SVC_CAT';
const IDENTITY_TYPE_UPPER_CODE = 'IDT_TP';
const PROVINCE_UPPER_CODE = 'PROV';
const ICD10_UPPER_CODE = 'ICD10';

// One group of the /common/common-codes response. The API returns three
// positionally-paired arrays rather than a list of objects.
interface CommonCodeGroup {
  code: (string | null)[];
  name: (string | null)[];
  description?: (string | null)[];
}

// Code stays the value (that is what the report rows store and what any future
// write would send); the name is only what the operator reads. Falling back to
// the code keeps a row visible when its label is empty in the active language —
// region and ICD-10 rows have no Korean translation.
const toCodeOptions = (group?: CommonCodeGroup) =>
  (group?.code ?? []).flatMap((code, index) =>
    code ? [{ value: code, label: group?.name[index] || code }] : []
  );

// The report is completed, or was closed by rejecting it.
const REPORT_STATUS_COMPLETED = 'RPT_ST_COMP';

// One patient's editable fields. Held per patient_info_id rather than as one
// set of fields for the active tab: the tabs switch which patient is shown,
// and an operator who edits patient 1, looks at patient 2 and comes back must
// still have their edits.
interface PatientForm {
  full_name: string;
  identity_type: string;
  national_or_passport_number: string;
  patient_gender: string;
  date_of_birth: string;
  job: string;
  province_id: string;
  city_id: string;
  full_address: string;
  chief_complain_description: string;
  main_complain_icd_id: string;
}

// Key the draft form sits under while the report has no patient at all. Patient
// rows are normally created by the ambulance app when the crew registers
// someone; until that happens the operator can still fill the section in, and
// saving creates the row. patient_info_id is a serial, so 0 is never a real one.
const NEW_PATIENT_KEY = 0;

const EMPTY_PATIENT_FORM: PatientForm = {
  full_name: '',
  identity_type: '',
  national_or_passport_number: '',
  patient_gender: '',
  date_of_birth: '',
  job: '',
  province_id: '',
  city_id: '',
  full_address: '',
  chief_complain_description: '',
  main_complain_icd_id: '',
};

// The report's own editable fields, in the shape the inputs hold them: both
// coordinates are strings here because that is what a half-typed one looks
// like. Doubles as the baseline the Save button's enabled state is measured
// against - rebuilt from the server's copy, so a save that round-trips leaves
// nothing dirty.
interface ReportForm {
  ambulance_reception_officer_id: string;
  extension_id: string;
  service_category_id: string;
  reporter_name: string;
  reporter_phone_number: string;
  reporter_gender: string;
  report_notes: string;
  incident_location: string;
  latitude: string;
  longitude: string;
}

const toReportForm = (report: ReportDetailsHeaderData | null): ReportForm => ({
  ambulance_reception_officer_id: report?.ambulance_reception_officer_id ?? '',
  extension_id: report?.extension_id ?? '',
  service_category_id: report?.service_category_id ?? '',
  reporter_name: report?.reporter_name ?? '',
  reporter_phone_number: report?.reporter_phone_number ?? '',
  reporter_gender: report?.reporter_gender ?? '',
  report_notes: report?.report_notes ?? '',
  incident_location: report?.incident_location ?? '',
  latitude: report?.latitude != null ? String(report.latitude) : '',
  longitude: report?.longitude != null ? String(report.longitude) : '',
});

// Both forms are flat and all-string, so a key-by-key comparison is the whole
// of it. Trimmed on the way in because the server trims what it stores: without
// that, typing a trailing space would leave the form reading as unsaved for as
// long as it is open.
const isSameForm = <Form extends object>(left: Form, right: Form): boolean =>
  (Object.keys(left) as (keyof Form)[]).every((key) => {
    const leftValue = left[key];
    const rightValue = right[key];
    return typeof leftValue === 'string' && typeof rightValue === 'string'
      ? leftValue.trim() === rightValue.trim()
      : leftValue === rightValue;
  });

// date_of_birth is a DATE column, which Flask serializes as an RFC 822 UTC
// string. Reading it back with local getters lands on the previous day
// anywhere west of GMT, so the date parts are taken in UTC. Display-only:
// the field's own format is DD/MM/YYYY (Audrey), the value sent back to the
// backend on save is converted back to YYYY-MM-DD - see handleSaveChanges.
const toDateInputValue = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const [year, month, day] = date.toISOString().slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const toPatientForm = (patient: ReportPatientData): PatientForm => ({
  full_name: patient.full_name ?? '',
  identity_type: patient.identity_type ?? '',
  national_or_passport_number: patient.national_or_passport_number ?? '',
  patient_gender: patient.patient_gender ?? '',
  date_of_birth: toDateInputValue(patient.date_of_birth),
  job: patient.job ?? '',
  province_id: patient.province_id ?? '',
  city_id: patient.city_id ?? '',
  full_address: patient.full_address ?? '',
  chief_complain_description: patient.chief_complain_description ?? '',
  main_complain_icd_id: patient.main_complain_icd_id ?? '',
});

// '' and a lone '-' are what a half-typed coordinate looks like; neither is a
// number, and the column is nullable.
const toCoordinate = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

// Coordinates compare as numbers, not as text: what comes back from a save is
// the stored double, so '-7.0860' would otherwise never match the '-7.086' the
// report now carries.
const isSameReportForm = (left: ReportForm, right: ReportForm): boolean =>
  toCoordinate(left.latitude) === toCoordinate(right.latitude) &&
  toCoordinate(left.longitude) === toCoordinate(right.longitude) &&
  isSameForm({ ...left, latitude: '', longitude: '' }, { ...right, latitude: '', longitude: '' });

const ReportDetails = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Ids are the cm_code values the columns store, so what the filter holds is
  // already what gets saved. Wording lives in cm_language (System Management >
  // Language).
  const genderFilters: FilterItem[] = [
    { id: 'GNDR_TP_M', label: t('report>detail>male') },
    { id: 'GNDR_TP_F', label: t('report>detail>female') },
  ];
  const [searchParams] = useSearchParams();
  const reportIdParam = searchParams.get('reportId');
  const parsedReportId = reportIdParam ? Number(reportIdParam) : NaN;
  const reportId = Number.isFinite(parsedReportId) ? parsedReportId : undefined;

  const {
    report,
    patients,
    ambulanceReports,
    status,
    images,
    voiceNotes,
    timeline,
    isLoading,
    isDetailError,
    requestLocation,
    isRequestingLocation,
    saveReport,
    isSavingReport,
    completeReport,
    isCompletingReport,
    canCompleteReport,
  } = useReportDetails(reportId);

  const inputRef = useRef<HTMLInputElement>(null);
  // The attachment being shown full size, if any.
  const [viewingMedia, setViewingMedia] = useState<ViewableMedia | null>(null);
  const [playing, setPlaying] = useState<number | null>(null);
  const [playingVoicesNotes, setPlayingVoicesNotes] = useState<number | null>(null);
  const [activePatientIndex, setActivePatientIndex] = useState(0);

  const activePatient = patients[activePatientIndex] ?? null;
  const activeAmbulanceReport =
    ambulanceReports.find((ar) => ar.patient_info_id === activePatient?.patient_info_id) ??
    ambulanceReports[0] ??
    null;

  const { photos, uploadPhotos, isUploadingPhotos } = useAmbulanceReportMedia(
    activeAmbulanceReport?.ambulance_report_id
  );

  // Every dropdown list on this page comes from cm_code, not from the report:
  // a report row only carries the one code it was created with, so a dropdown
  // built from it would offer a single choice. The four small groups are one
  // request - /common/common-codes takes repeated upper-code params.
  const { commonListData: codeGroups } = useCommonCodes({
    optionParams: [
      EXTENSION_UPPER_CODE,
      SERVICE_CATEGORY_UPPER_CODE,
      IDENTITY_TYPE_UPPER_CODE,
      PROVINCE_UPPER_CODE,
    ]
      .map((group) => `upper-code=${group}`)
      .join('&'),
  });

  // Officers come from cm_user, not cm_code: the field points at a person.
  const { officers } = useReportOfficers();

  const [receptionOfficerId, setReceptionOfficerId] = useState('');
  const [extensionId, setExtensionId] = useState('');
  const [serviceCategoryId, setServiceCategoryId] = useState('');
  // Caller-information fields. The textarea and the gender filter were the two
  // that could not be edited at all: a textarea given a `value` and no
  // `onChange` is read-only in React, and the filter's handler was a stub. The
  // name and phone inputs accepted typing only because GeneralInput keeps its
  // own copy - it never reached the page. All four now live here.
  const [reporterName, setReporterName] = useState('');
  const [reporterPhone, setReporterPhone] = useState('');
  const [reporterGender, setReporterGender] = useState('');
  const [reportNotes, setReportNotes] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // Patient fields, one form per patient. Everything the operator can change
  // about the patient showing in the tabs above; ambulance and healthcare
  // details below are deliberately absent - those are dispatch facts, not
  // operator input.
  const [patientForms, setPatientForms] = useState<Record<number, PatientForm>>({});

  // With no patient registered yet the section edits the draft, which is saved
  // as a new patient rather than as an update to one.
  const activePatientId =
    activePatient?.patient_info_id ?? (patients.length === 0 ? NEW_PATIENT_KEY : null);
  const patientForm =
    (activePatientId != null ? patientForms[activePatientId] : undefined) ?? EMPTY_PATIENT_FORM;

  const setPatientField = <Field extends keyof PatientForm>(
    field: Field,
    value: PatientForm[Field]
  ) => {
    if (activePatientId == null) return;
    setPatientForms((previous) => ({
      ...previous,
      [activePatientId]: { ...(previous[activePatientId] ?? EMPTY_PATIENT_FORM), [field]: value },
    }));
  };

  // Cities/regencies hang off the chosen province (upper_code = the province's
  // BPS code), so this is a second request that only fires once one is picked.
  const { commonListData: cityCodes } = useCommonCodes({
    optionParams: `upper-code=${patientForm.province_id}`,
    enabled: Boolean(patientForm.province_id),
  });

  // The officer already on the report is kept in the list even when they are
  // not (or are no longer) a PSC operator - a report handled by an admin would
  // otherwise show an empty field, quietly losing who took the call.
  const officerOptions = useMemo(() => {
    const options = officers.map((officer) => ({
      value: officer.user_id,
      label: officer.name || officer.account_id,
    }));
    const assignedId = report?.ambulance_reception_officer_id;
    if (assignedId && !options.some((option) => option.value === assignedId)) {
      options.unshift({
        value: assignedId,
        label: report?.ambulance_reception_officer_name || assignedId,
      });
    }
    return options;
  }, [officers, report]);

  // The extension dropdown shows only the trailing segment of the code
  // (EXT_ID_TP_001 -> "001") - that suffix is the extension number operators
  // dial and read out. Its readable name is what Call Source displays instead.
  const extensionOptions = useMemo(
    () =>
      (codeGroups?.[EXTENSION_UPPER_CODE]?.code ?? [])
        .filter((code): code is string => Boolean(code))
        .map((code) => ({ value: code, label: code.split('_').pop() ?? code })),
    [codeGroups]
  );

  const serviceCategoryOptions = useMemo(
    () => toCodeOptions(codeGroups?.[SERVICE_CATEGORY_UPPER_CODE]),
    [codeGroups]
  );
  const identityTypeOptions = useMemo(
    () => toCodeOptions(codeGroups?.[IDENTITY_TYPE_UPPER_CODE]),
    [codeGroups]
  );
  const provinceOptions = useMemo(
    () => toCodeOptions(codeGroups?.[PROVINCE_UPPER_CODE]),
    [codeGroups]
  );
  const cityOptions = useMemo(
    () => (patientForm.province_id ? toCodeOptions(cityCodes?.[patientForm.province_id]) : []),
    [cityCodes, patientForm.province_id]
  );
  // Built over the unfiltered arrays: `code[i]` and `name[i]` are positionally
  // paired by the API, so filtering one of them first would misalign the names.
  const extensionNameByCode = useMemo(() => {
    const group = codeGroups?.[EXTENSION_UPPER_CODE];
    const nameByCode = new Map<string, string>();
    group?.code.forEach((code, index) => {
      if (code) nameByCode.set(code, group.name[index] ?? '');
    });
    return nameByCode;
  }, [codeGroups]);

  // Seeded once per record, not on every render of it. The report query
  // refetches on window focus, which hands back a fresh object each time -
  // re-seeding on that would wipe whatever the operator had typed the moment
  // they came back from another tab.
  const seededReportId = useRef<number | null>(null);

  useEffect(() => {
    if (!report || seededReportId.current === report.report_id) return;
    seededReportId.current = report.report_id;

    setReceptionOfficerId(report.ambulance_reception_officer_id ?? '');
    setExtensionId(report.extension_id ?? '');
    setServiceCategoryId(report.service_category_id ?? '');
    setReporterName(report.reporter_name ?? '');
    setReporterPhone(report.reporter_phone_number ?? '');
    setReporterGender(report.reporter_gender ?? '');
    setReportNotes(report.report_notes ?? '');
    setIncidentLocation(report.incident_location ?? '');
    // Empty, not '-': these are editable, and a placeholder dash typed into a
    // coordinate is what would be sent on save.
    setLatitude(report.latitude != null ? String(report.latitude) : '');
    setLongitude(report.longitude != null ? String(report.longitude) : '');
  }, [report]);

  // One form per patient, built the first time that patient is seen. Existing
  // entries are left alone, so neither a tab switch nor a refetch can overwrite
  // what the operator has typed.
  useEffect(() => {
    if (patients.length === 0) return;
    setPatientForms((previous) => {
      let added = false;
      const next = { ...previous };
      for (const patient of patients) {
        if (next[patient.patient_info_id]) continue;
        next[patient.patient_info_id] = toPatientForm(patient);
        added = true;
      }
      return added ? next : previous;
    });
  }, [patients]);

  // The datepicker below is built once per language, so its onSelect closes
  // over the patient who was active back then. This is what it reads instead.
  const activePatientIdRef = useRef<number | null>(null);
  useEffect(() => {
    activePatientIdRef.current = activePatientId;
  }, [activePatientId]);

  // Falls back to the label the report already carries so the field is not
  // blank while cm_code is still in flight.
  const callSource = extensionId
    ? (extensionNameByCode.get(extensionId) ?? report?.extension_label ?? '')
    : '';

  // The report stays a form until the operator closes it themselves. Ambulances
  // finishing only unlocks "Mark as Completed" - the last corrections tend to
  // come after that, once the crew has said what actually happened. The server
  // enforces the same rule.
  const isReportClosed =
    report?.report_status === REPORT_STATUS_COMPLETED || report?.finished_operator_datetime != null;
  // A healthcare account can view Report Details but never edit it - the
  // backend enforces the same boundary on PUT/DELETE/location-request
  // (report_controller.py's internal_only), so this is UX only, not the
  // security boundary itself. Completing is the one write action a healthcare
  // account IS allowed (self-complete a report visible to it - see
  // post_report_complete.py), so it's excluded from this gate.
  const accountType = useAuthStore((s) => s.account_type);
  const isHealthcareAccount = accountType === 'healthcare';
  const isEditable = Boolean(report) && !isReportClosed && !isHealthcareAccount;
  const isReadOnly = !isEditable;

  const reportForm: ReportForm = {
    ambulance_reception_officer_id: receptionOfficerId,
    extension_id: extensionId,
    service_category_id: serviceCategoryId,
    reporter_name: reporterName,
    reporter_phone_number: reporterPhone,
    reporter_gender: reporterGender,
    report_notes: reportNotes,
    incident_location: incidentLocation,
    latitude,
    longitude,
  };

  // Only while the report still has no patient of its own: once the ambulance
  // app registers one, the draft is stale and must not become a second row.
  const newPatientDraft = patients.length === 0 ? patientForms[NEW_PATIENT_KEY] : undefined;
  const hasNewPatient = Boolean(
    newPatientDraft && !isSameForm(newPatientDraft, EMPTY_PATIENT_FORM)
  );

  // Measured against the server's copy rather than a snapshot taken at seed
  // time, so a saved change stops counting as one the moment the refetch lands
  // and the button goes back to disabled on its own.
  const isDirty =
    hasNewPatient ||
    !isSameReportForm(reportForm, toReportForm(report)) ||
    patients.some((patient) => {
      const form = patientForms[patient.patient_info_id];
      return form ? !isSameForm(form, toPatientForm(patient)) : false;
    });

  // One primary action at a time. Completing is offered only once every
  // ambulance has finished and nothing is left unsaved - an edit turns the
  // button back into Save, and saving turns it back again - so the report can
  // never be closed with the operator's last correction still in the form.
  const showCompleteButton = Boolean(report) && !isReportClosed && canCompleteReport && !isDirty;
  const showSaveButton =
    !isHealthcareAccount && Boolean(report) && !isReportClosed && !showCompleteButton;

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }
    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      // No selectedDates: the picker is built before the patient is loaded, so
      // pre-selecting today would write today's date into a date-of-birth field
      // that is meant to read empty until someone fills it in.
      // position: 'top center' as unknown as AirDatepickerPosition,
      position: zoomSafeDatepickerPosition('top center'),
      // No timepicker: date_of_birth is a DATE column, so a time would only be
      // collected to be thrown away.
      // Display format DD/MM/YYYY (Audrey) - toDateInputValue seeds the same
      // format from the server value, and handleSaveChanges converts back to
      // YYYY-MM-DD before it's sent - the backend's own format is unchanged.
      dateFormat: 'dd/MM/yyyy',
      onSelect: (date) => {
        const patientInfoId = activePatientIdRef.current;
        if (patientInfoId == null) return;
        setPatientForms((previous) => ({
          ...previous,
          [patientInfoId]: {
            ...(previous[patientInfoId] ?? EMPTY_PATIENT_FORM),
            date_of_birth: (date.formattedDate as string) ?? '',
          },
        }));
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);

    return () => {
      datepicker1.destroy();
    };
  }, [i18n.language]);

  const handleOpenPicker = () => {
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !activeAmbulanceReport) return;

    const files = Array.from(e.target.files);
    e.target.value = '';

    try {
      await uploadPhotos(files);
    } catch (error: unknown) {
      showToast({
        type: 'error',
        title: t('psc>msg>upload failed'),
        message: (error as Error)?.message ?? t('psc>msg>failed upload photo'),
      });
    }
  };

  const handleRequestLocation = () => {
    requestLocation(
      {},
      {
        onSuccess: (res) => {
          showToast({
            type: 'success',
            title: t('psc>msg>location requested'),
            message: res?.data
              ? t('psc>msg>whatsapp sent to', { phone: res.data.phone_number_masked })
              : t('psc>msg>location request sent'),
          });
        },
        onError: (error: unknown) => {
          showToast({
            type: 'error',
            title: t('psc>msg>request failed'),
            message: (error as Error)?.message ?? t('psc>msg>failed send location request'),
          });
        },
      }
    );
  };

  // '' stays '' (clears the date, unchanged behaviour). A clean DD/MM/YYYY
  // converts to the backend's YYYY-MM-DD. Anything else (a malformed typed
  // value) is passed through as-is rather than silently cleared - the
  // backend's own date parsing rejects it with a 400, exactly like before
  // this field's display format changed.
  const toSubmittedDob = (value: string): string => {
    if (!value.trim()) return value;
    return parseDDMMYYYYToISO(value) || value;
  };

  const handleSaveChanges = () => {
    if (!report) return;

    // Gender is the one field the table's CHECK constraint won't let a new
    // patient row go without (see update_report_detail.py) - caught here so
    // the operator gets a specific message instead of the backend's raw
    // "Invalid patients[0].patient_gender - required".
    if (hasNewPatient && !newPatientDraft?.patient_gender.trim()) {
      showToast({
        type: 'error',
        title: t('psc>msg>save failed'),
        message: `${t('report>detail>patient gender')} ${t('errmsg > required')}`,
      });
      return;
    }

    saveReport(
      {
        report: {
          ...reportForm,
          // Strings on the way in, numbers on the way out.
          latitude: toCoordinate(reportForm.latitude),
          longitude: toCoordinate(reportForm.longitude),
        },
        // Every patient with a form, not just the tab on screen - the others
        // may have been edited before switching tabs. The draft goes without an
        // id, which is what tells the server to create the row. date_of_birth
        // is DD/MM/YYYY in the form (display format); converted back to the
        // backend's YYYY-MM-DD here, right at the boundary.
        patients: [
          ...patients.flatMap((patient) => {
            const form = patientForms[patient.patient_info_id];
            return form
              ? [
                  {
                    patient_info_id: patient.patient_info_id,
                    ...form,
                    date_of_birth: toSubmittedDob(form.date_of_birth),
                  },
                ]
              : [];
          }),
          ...(hasNewPatient && newPatientDraft
            ? [{ ...newPatientDraft, date_of_birth: toSubmittedDob(newPatientDraft.date_of_birth) }]
            : []),
        ],
      },
      {
        onSuccess: (res) => {
          // The report finished (or was closed) while this page was open: the
          // save was refused and the invalidated query will flip it read-only.
          if (res?.data?.conflict) {
            showToast({
              type: 'error',
              title: t('psc>msg>report not editable'),
              message: t('psc>msg>report not editable detail'),
            });
            return;
          }
          // The draft has become a real patient row; keeping it would create a
          // second one on the next save.
          if (res?.data?.patients_created) {
            setPatientForms((previous) => {
              const next = { ...previous };
              delete next[NEW_PATIENT_KEY];
              return next;
            });
          }
          showToast({
            type: 'success',
            title: t('psc>msg>changes saved'),
            message: t('psc>msg>changes saved detail'),
          });
        },
        onError: (error: unknown) => {
          showToast({
            type: 'error',
            title: t('psc>msg>save failed'),
            message: (error as Error)?.message ?? t('psc>msg>failed save changes'),
          });
        },
      }
    );
  };

  const handleCompleteReport = () => {
    completeReport(undefined, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: t('psc>msg>report completed'),
          message: t('psc>msg>report completed detail'),
        });
        navigate('/dashboard/psc-dashboard');
      },
      onError: (error: unknown) => {
        showToast({
          type: 'error',
          title: t('psc>msg>complete failed'),
          message: (error as Error)?.message ?? t('psc>msg>failed complete report'),
        });
      },
    });
  };

  const handleOpenGoogleMaps = () => {
    if (report?.latitude != null && report?.longitude != null) {
      window.open(`https://www.google.com/maps?q=${report.latitude},${report.longitude}`, '_blank');
      return;
    }
    // No coordinates (common for a WhatsApp-text-only report) - fall back to
    // a Google Maps text search on the reported address instead of just
    // refusing to open anything.
    if (report?.incident_location) {
      window.open(
        `https://www.google.com/maps?q=${encodeURIComponent(report.incident_location)}`,
        '_blank'
      );
      return;
    }
    showToast({
      type: 'error',
      title: t('psc>msg>no coordinates'),
      message: t('psc>msg>no coordinates detail'),
    });
  };

  if (!reportId) {
    return (
      <section className="_ambulance bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <span className="title1bold text-text-grey">{t('cmn>msg>missing report id')}</span>
        </div>
      </section>
    );
  }

  // Out-of-scope report id (403), deleted (404), or any other load failure -
  // an explicit state instead of a page of permanently-disabled empty fields.
  if (isDetailError && !isLoading) {
    return (
      <section className="_ambulance bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <span className="title1bold text-text-grey">
            {t('psc>msg>report not accessible', 'This report is not accessible.')}
          </span>
          <Button
            variant="default"
            onClick={() => navigate('/dashboard/psc-dashboard')}
            label={t('cmn>master>close')}
            customButton=""
            className="btn-outline-blue uppercase text-[20px]"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="_report-detail-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className=" report-detail-screen p-3 w-full h-[calc(95vh-12px)] grid grid-cols-[2.5fr_1fr] gap-3 overflow-hidden">
        <section className="w-full flex  flex-col h-full overflow-hidden rounded-[12px] border bg-db-card bg-db-border">
          <div className="p-6 flex flex-col gap-6 w-full h-full overflow-auto">
            {/* <BackLink label="back" onclick={() => alert('klik aku mas')} /> */}
            <div className="flex flex-row gap-1 items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-text-grey capitalize dark:text-white title2medium">
                  {t('cmn>master>report id')}
                </span>
                <span className="title1bold">
                  {isLoading
                    ? t('cmn>master>loading')
                    : (report?.report_code_id ?? t('psc>report>not yet assigned'))}
                </span>
              </div>
              <BubbleEvent eventTypes={status} />
            </div>

            <div className="border flex flex-col gap-6 p-6 w-full rounded-[12px] ">
              <div className="inline-flex gap-3 items-center">
                <Phone />
                <span className="h5medium capitalize">
                  {t('report>detail>call report information')}
                </span>
              </div>
              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 uppercase h5medium">
                  {t('report>detail>caller information')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>ambulance reception officer')}
                  direction="col"
                  inputType="select"
                  name="ambulance_reception_officer_id"
                  value={receptionOfficerId}
                  optionSelect={officerOptions}
                  disabled={isReadOnly}
                  onChange={(e) => setReceptionOfficerId(e.target.value)}
                />
                {/* Received time (registered_time) - the moment the report came
                    in, same as Report Management's field and the list's "Waktu
                    Masuk" column. Was report_handling_time under a "Report
                    Handling Time" label. Reuses the report>col>received time
                    tag, already translated in cm_language. */}
                <DetailForm
                  label={t('report>col>received time')}
                  value={
                    report?.registered_time
                      ? formatFullDateTimeDDMMYYYY(new Date(report.registered_time))
                      : '-'
                  }
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  label={t('report>field>completion time')}
                  value={
                    report?.report_handling_completion_time
                      ? formatFullDateTimeDDMMYYYY(new Date(report.report_handling_completion_time))
                      : '-'
                  }
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>extension')}
                  direction="col"
                  inputType="select"
                  name="extension_id"
                  value={extensionId}
                  optionSelect={extensionOptions}
                  disabled={isReadOnly}
                  onChange={(e) => setExtensionId(e.target.value)}
                />

                {/* Derived from the extension above, never typed into. */}
                <DetailForm
                  label={t('report>detail>call source')}
                  value={callSource}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  label={t('report>detail>service category')}
                  direction="col"
                  inputType="select"
                  name="service_category_id"
                  value={serviceCategoryId}
                  optionSelect={serviceCategoryOptions}
                  disabled={isReadOnly}
                  onChange={(e) => setServiceCategoryId(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('psc>report>report by')}
                  name="reporter_name"
                  value={reporterName}
                  disabled={isReadOnly}
                  onChange={(e) => setReporterName(e.target.value)}
                  direction="col"
                  inputType="text"
                />

                <DetailForm
                  label={t('psc>report>reporter phone number')}
                  direction="col"
                  inputType="text"
                  name="reporter_phone_number"
                  value={reporterPhone}
                  disabled={isReadOnly}
                  onChange={(e) => setReporterPhone(e.target.value)}
                  className="relative"
                >
                  <ButtonWhatsapp
                    styleButton="absolute top-[45%] right-[20px]"
                    label={t('psc>report>call')}
                    disabled={!reporterPhone.trim()}
                    onclick={() => {
                      const waNumber = toWhatsAppNumber(reporterPhone);
                      if (waNumber)
                        window.open(
                          `https://wa.me/call/+${waNumber}`,
                          '_blank',
                          'noopener,noreferrer,width=480,height=720'
                        );
                    }}
                  />
                </DetailForm>

                <DetailForm
                  label={t('report>detail>reporter gender')}
                  required={true}
                  direction="col"
                  inputType="text"
                  showInput={false}
                  className="relative"
                >
                  {/* The reporter's gender, not the patient's - this used to
                      read the patient's and was unclickable besides. */}
                  <StatusFilter
                    items={genderFilters}
                    value={reporterGender}
                    classStyle="mr-2 px-6"
                    disabled={isReadOnly}
                    onChange={setReporterGender}
                  />
                </DetailForm>
              </div>

              <div className="w-full">
                <DetailForm
                  label={t('psc>report>stt text (speech-to-text)')}
                  direction="col"
                  inputType="textarea"
                  className="relative "
                  styleTextArea="h-[200px]"
                  value={report?.stt_report_text ?? ''}
                  disabled={true}
                />
              </div>
              <div className="w-full">
                <DetailForm
                  label={t('report>detail>description')}
                  direction="col"
                  inputType="textarea"
                  className="relative "
                  styleTextArea="h-[200px]"
                  name="report_notes"
                  value={reportNotes}
                  disabled={isReadOnly}
                  onChange={(e) => setReportNotes(e.target.value)}
                />
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('psc>report>report location')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="h-[230px]">
                <DetailForm
                  label={t('psc>report>report location')}
                  direction="col"
                  inputType="textarea"
                  className="relative "
                  styleTextArea="h-[200px]"
                  name="incident_location"
                  value={incidentLocation}
                  disabled={isReadOnly}
                  onChange={(e) => setIncidentLocation(e.target.value)}
                  labelChildren={
                    <div className=" inline-flex gap-3 items-center ml-auto">
                      <ButtonWhatsapp
                        styleButton="!border-[#007AFF] !bg-[#F3F6FE] dark:!bg-transparent/40 "
                        label={t('report>detail>open in google maps')}
                        img={Gmaps}
                        onclick={handleOpenGoogleMaps}
                      />
                    </div>
                  }
                ></DetailForm>
              </div>

              <div className="grid grid-cols-2 items-center gap-6">
                {/* Digits, a leading minus and one decimal point only - the
                    pattern rejects the keystroke rather than the save. */}
                <DetailForm
                  label={t('report>detail>latitude')}
                  name="latitude"
                  value={latitude}
                  disabled={isReadOnly}
                  onChange={(e) => setLatitude(e.target.value)}
                  pattern="^-?\d*\.?\d*$"
                  direction="col"
                  inputType="text"
                />
                <DetailForm
                  label={t('report>detail>longitude')}
                  name="longitude"
                  value={longitude}
                  disabled={isReadOnly}
                  onChange={(e) => setLongitude(e.target.value)}
                  pattern="^-?\d*\.?\d*$"
                  direction="col"
                  inputType="text"
                />
              </div>

              <div className="gap-3 flex-col flex">
                <span className="text-text-blue-100 title1medium uppercase">
                  {t('psc>report>media file')} ({images.length})
                </span>
                <div className="divider border"></div>
                <div className="w-full h-full flex  gap-3 flex-wrap">
                  {/* Videos included: the count above always did, and dropping
                      them from the list only hid them. */}
                  {images.map((item, index) => {
                    const fileName = item.name || `${t('psc>report>media file')} ${index + 1}`;
                    return (
                      <CardMediaFile
                        key={item.url}
                        fileName={fileName}
                        totalFile={item.registeredTime}
                        mediaType={item.type}
                        img={item.url}
                        onClick={() =>
                          setViewingMedia({
                            url: item.url,
                            type: item.type,
                            title: fileName,
                            subtitle: item.registeredTime,
                          })
                        }
                      />
                    );
                  })}
                </div>
              </div>

              <div className="gap-3 flex-col w-full flex">
                <span className="text-text-blue-100 title1medium uppercase">
                  {t('psc>report>incoming calls')} ({incomingList.length})
                </span>
                <div className="divider border"></div>
                <div className="grid grid-cols-2  gap-3 ">
                  {incomingList.map((audio) => (
                    <AudioPlayerCard
                      key={audio.id}
                      title={audio.title}
                      audioUrl={audio.audioUrl}
                      isPlaying={playing === audio.id}
                      onPlay={() => setPlaying((prev) => (prev === audio.id ? null : audio.id))}
                    />
                  ))}
                </div>
              </div>

              <div className="gap-3 flex-col w-full flex">
                <span className="text-text-blue-100 title1medium uppercase">
                  {t('psc>report>voice notes')} ({voiceNotes.length})
                </span>
                <div className="divider border"></div>
                <div className="grid grid-cols-2  gap-3 ">
                  {voiceNotes.map((audio) => (
                    <AudioPlayerCard
                      key={audio.id}
                      title={audio.title}
                      audioUrl={audio.audioUrl}
                      isPlaying={playingVoicesNotes === audio.id}
                      onPlay={() =>
                        setPlayingVoicesNotes((prev) => (prev === audio.id ? null : audio.id))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* patient */}

            <div className="border flex flex-col gap-6 p-6 w-full rounded-[12px] ">
              <div className="inline-flex gap-3 items-center">
                <User />
                <span className="h5medium capitalize">{t('report>detail>patient data')}</span>
              </div>

              <div className="inline-flex gap-3 items-center">
                {patients.length === 0 ? (
                  <span className="title2 text-text-grey">{t('report>detail>no patient')}</span>
                ) : (
                  patients.map((patient, index) => (
                    <button
                      key={patient.patient_info_id}
                      type="button"
                      onClick={() => setActivePatientIndex(index)}
                      className={`px-4 py-2 border-b title2 ${
                        activePatientIndex === index
                          ? 'text-text-blue-100 border-b-text-blue-100 '
                          : 'border-b-transparent'
                      }`}
                    >
                      {t('report>detail>patient number', { number: index + 1 })}
                    </button>
                  ))
                )}
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('report>detail>patient information')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>identity type')}
                  direction="col"
                  inputType="select"
                  name="identity_type"
                  value={patientForm.identity_type}
                  optionSelect={identityTypeOptions}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('identity_type', e.target.value)}
                />
                <DetailForm
                  label={t('report>detail>national id or passport id')}
                  name="national_or_passport_number"
                  value={patientForm.national_or_passport_number}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('national_or_passport_number', e.target.value)}
                  direction="col"
                  inputType="text"
                />
                <DetailForm
                  label={t('report>detail>full name')}
                  name="full_name"
                  value={patientForm.full_name}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('full_name', e.target.value)}
                  direction="col"
                  inputType="text"
                />
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>patient gender')}
                  direction="col"
                  inputType="text"
                  showInput={false}
                  className="relative"
                >
                  <StatusFilter
                    items={genderFilters}
                    value={patientForm.patient_gender}
                    classStyle="mr-2 px-6"
                    disabled={isReadOnly}
                    onChange={(value) => setPatientField('patient_gender', value)}
                  />
                </DetailForm>

                <DetailForm
                  label={t('report>detail>date of birth')}
                  direction="col"
                  inputType="text"
                  showInput={false}
                >
                  {/* The calendar writes through onSelect above; this keeps a
                      date typed straight into the box from being dropped. */}
                  <GeneralInput
                    isDob={true}
                    type="text"
                    name={'start_date'}
                    value={patientForm.date_of_birth}
                    disabled={isReadOnly}
                    onChange={(e) => setPatientField('date_of_birth', e.target.value)}
                    customInput=""
                    inputFormStyle="bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! h-[45px]! text-[20px]!"
                  />
                </DetailForm>

                <DetailForm
                  label={t('report>detail>job')}
                  name="job"
                  value={patientForm.job}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('job', e.target.value)}
                  direction="col"
                  inputType="text"
                />
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('report>detail>address')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="grid grid-cols-2 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>province')}
                  direction="col"
                  inputType="select"
                  name="province_id"
                  value={patientForm.province_id}
                  optionSelect={provinceOptions}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    // The city list is scoped to the province, so a province
                    // change invalidates whatever city was selected under it.
                    if (activePatientId == null) return;
                    setPatientForms((previous) => ({
                      ...previous,
                      [activePatientId]: {
                        ...(previous[activePatientId] ?? EMPTY_PATIENT_FORM),
                        province_id: e.target.value,
                        city_id: '',
                      },
                    }));
                  }}
                />
                <DetailForm
                  label={t('report>detail>city/regency')}
                  direction="col"
                  inputType="select"
                  name="city_id"
                  value={patientForm.city_id}
                  optionSelect={cityOptions}
                  disabled={isReadOnly || !patientForm.province_id}
                  onChange={(e) => setPatientField('city_id', e.target.value)}
                />
              </div>

              <div className="h-[230px]">
                <DetailForm
                  label={t('report>detail>full address')}
                  direction="col"
                  inputType="textarea"
                  className="relative "
                  styleTextArea="h-[200px]"
                  name="full_address"
                  value={patientForm.full_address}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('full_address', e.target.value)}
                />
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('report>detail>patient complaint information')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="grid grid-cols-2 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>chief complaint')}
                  name="chief_complain_description"
                  value={patientForm.chief_complain_description}
                  disabled={isReadOnly}
                  onChange={(e) => setPatientField('chief_complain_description', e.target.value)}
                  direction="col"
                  inputType="text"
                />
                {/* Not a plain select: ICD-10 is ~18,500 codes, so this one
                    searches the group on the server a page at a time. */}
                <DetailForm
                  label={t('report>detail>main complaint (icd 10)')}
                  direction="col"
                  showInput={false}
                >
                  <CodeSearchSelect
                    upperCode={ICD10_UPPER_CODE}
                    name="main_complain_icd_id"
                    value={patientForm.main_complain_icd_id}
                    valueLabel={activePatient?.main_complain_icd_label}
                    placeholder={t('cmn > select')}
                    disabled={isReadOnly}
                    onChange={(code) => setPatientField('main_complain_icd_id', code)}
                  />
                </DetailForm>
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('report>detail>ambulance details')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  value={activeAmbulanceReport?.health_care_facility_name ?? '-'}
                  label={t('report>detail>ambulance ownership')}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  value={activeAmbulanceReport?.ambulance_type_name ?? '-'}
                  label={t('report>detail>ambulance type')}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  label={t('report>detail>police number')}
                  value={activeAmbulanceReport?.police_number ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  value={activeAmbulanceReport?.serial_no ?? '-'}
                  label={t('report>detail>ambulance code/id')}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />

                <DetailForm
                  label="VEHICLE TYPE"
                  value={activeAmbulanceReport?.vehicle_type ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />

                <DetailForm
                  value={activeAmbulanceReport?.manufacturer_year ?? '-'}
                  label={t('report>detail>years')}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
              </div>

              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>condition')}
                  value={activeAmbulanceReport?.ambulance_condition_name ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  label={t('report>detail>pic name')}
                  value={activeAmbulanceReport?.ambulance_pic_name ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  value={activeAmbulanceReport?.ambulance_pic_phone ?? '-'}
                  label={t('report>detail>pic phone number')}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                {/* <DetailForm
                  label={t('report>detail>dispatch status')}
                  value={activeAmbulanceReport?.ambulance_report_status_label ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                /> */}
              </div>

              <div className="w-full flex flex-col gap-4">
                <span className="text-text-blue-100 capitalize h5medium">
                  {t('report>detail>healthcare details')}
                </span>
                {/* divider */}
                <div className="border w-full"></div>
              </div>

              {/* The facility the crew is TRANSFERRING the patient to, not the
                  one that owns the ambulance — that one is the "ambulance
                  ownership" field in the section above. The backend returns
                  these as null until the dispatch reaches AMB_RPT_ST_TRF, so a
                  mission still en route reads "-" rather than naming a hospital
                  nobody chose. */}
              <div className="grid grid-cols-3 gap-6 items-center">
                <DetailForm
                  label={t('report>detail>healthcare name')}
                  value={activeAmbulanceReport?.destination_health_care_facility_name ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                {/* Still unwired: health_care_facility.health_care_code is in
                    every DDL reference but not in the live DB, so asking for it
                    500s the whole endpoint. */}
                <DetailForm
                  label={t('report>detail>facility code')}
                  value={'-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
                <DetailForm
                  label={t('report>detail>telephone')}
                  value={activeAmbulanceReport?.destination_health_care_facility_phone ?? '-'}
                  direction="col"
                  inputType="text"
                  disabled={true}
                />
              </div>

              <DetailForm
                label={t('report>detail>healthcare address')}
                direction="col"
                inputType="textarea"
                className="relative"
                styleTextArea="h-[200px]"
                value={activeAmbulanceReport?.destination_health_care_facility_address ?? '-'}
                disabled={true}
              />

              <div className="gap-3 flex-col w-full flex">
                <span className="text-text-blue-100 title1medium uppercase">
                  {t('report>detail>ambulances photo')} ({photos.length})
                </span>
                <div className="divider border"></div>
                <div className="w-full h-full flex  gap-3 flex-wrap">
                  {photos.map((item) => (
                    <CardMediaFile
                      key={item.id}
                      fileName={item.fileName}
                      totalFile={item.registeredTime}
                      img={item.url ?? undefined}
                      onClick={
                        item.url
                          ? () =>
                              setViewingMedia({
                                url: item.url as string,
                                type: 'image',
                                title: item.fileName,
                                subtitle: item.registeredTime,
                              })
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full flex! gap-3  flex-col h-full overflow-auto rounded-[12px]">
          <div className="w-full flex h-full flex-col gap-6 overflow-auto">
            <div className="w-full flex  flex-1 flex-col gap-6 p-6 overflow-auto rounded-[12px] border bg-db-card bg-db-border">
              <span className="title1bold">{t('report>detail>timeline')}</span>
              {timeline.length === 0 ? (
                <span className="title2 text-text-grey">{t('report>detail>no timeline')}</span>
              ) : (
                timeline.map((item, index) => (
                  <TimelineVertical
                    key={item.id}
                    item={item}
                    isLast={index === timeline.length - 1}
                  />
                ))
              )}
            </div>
          </div>
          <div className="w-full flex h-fit flex-col gap-2">
            {/* Held back until the report has loaded, so neither button shows
                for a moment before it is known which one belongs there. */}
            {showSaveButton && (
              <Button
                onClick={handleSaveChanges}
                variant="default"
                label={t('report>detail>save changes')}
                disabled={isSavingReport || !isDirty}
                className="flex-1 btn-fill-blue uppercase text-[20px] disabled:pointer-events-auto disabled:!cursor-not-allowed"
              />
            )}

            {showCompleteButton && (
              <Button
                onClick={handleCompleteReport}
                variant="default"
                label={t('report>detail>complete report')}
                disabled={isCompletingReport}
                className="flex-1 btn-fill-blue uppercase text-[20px] disabled:pointer-events-auto disabled:!cursor-not-allowed"
              />
            )}

            {/* A closed report has no action left; without a line saying so, a
                page of greyed-out fields reads as broken rather than finished.
                Same for a healthcare account once it has no Complete action
                left either - it can never edit this page regardless. */}
            {report && (isReportClosed || (isHealthcareAccount && !showCompleteButton)) && (
              <span className="title2 text-text-grey text-center">
                {t('report>detail>read only notice')}
              </span>
            )}
            <Button
              variant="default"
              onClick={() => navigate(-1)}
              label={t('cmn>master>close')}
              customButton=""
              className="flex-1 btn-outline-blue   uppercase  text-[20px] "
            />
          </div>
        </section>
      </div>

      <MediaViewerModal media={viewingMedia} onClose={() => setViewingMedia(null)} />
    </section>
  );
};

export default ReportDetails;
